import React, { forwardRef, Ref, useState } from "react";
import classNames from "classnames";
import P from "components/Typography/P";
import styles from "./Input.module.scss";

export type InputProps = {
    active?: boolean;
    classNames?: string;
    errorMessage?: string;
    valid?: boolean;
} & JSX.IntrinsicElements["input"];

export type TextAreaProps = {
    active?: boolean;
    disableResize?: boolean;
    errorMessage?: string;
    valid?: boolean;
} & JSX.IntrinsicElements["textarea"];

const Input = forwardRef(
    (
        { active = true, valid = true, errorMessage, onBlur, className, ...rest }: InputProps,
        ref: Ref<HTMLInputElement>,
    ) => {
        const [hasBlured, setHasBlured] = useState(false);
        function handleOnBlur(e: React.FocusEvent<HTMLInputElement>) {
            if (!hasBlured) {
                setHasBlured(true);
            }
            if (typeof onBlur === "function") {
                onBlur(e);
            }
        }
        return (
            <>
                <input
                    ref={ref}
                    onBlur={handleOnBlur}
                    className={classNames(
                        styles["input"],
                        {
                            [styles["input--active"]]: active,
                            [styles["input--disable"]]: !active,
                        },
                        className,
                    )}
                    {...rest}
                />
                {!valid && hasBlured && errorMessage && (
                    <P className={styles["error-message"]}>{errorMessage}</P>
                )}
            </>
        );
    },
);

const TextArea = forwardRef(
    (
        {
            active = true,
            className,
            valid = true,
            errorMessage,
            onBlur,
            disableResize,
            ...rest
        }: TextAreaProps,
        ref: Ref<HTMLTextAreaElement>,
    ) => {
        const [hasBlured, setHasBlured] = useState(false);
        function handleOnBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
            if (!hasBlured) {
                setHasBlured(true);
            }
            if (typeof onBlur === "function") {
                onBlur(e);
            }
        }
        return (
            <>
                <textarea
                    ref={ref}
                    onBlur={handleOnBlur}
                    className={classNames(
                        styles["input"],
                        {
                            [styles["input--active"]]: active,
                            [styles["input--disable"]]: !active,
                            [styles["input--no-resize"]]: disableResize,
                        },
                        className,
                    )}
                    {...rest}
                />
                {!valid && hasBlured && errorMessage && (
                    <P className={styles["error-message"]}>{errorMessage}</P>
                )}
            </>
        );
    },
);

export { TextArea, Input };
